import { useMemo, useState } from 'react'
import { moneyChartValue } from '@klear/ui/components/chart-series'
import { useParams } from 'react-router'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import { BasisPoints } from '@klear/basis-points'
import { ChartContainer, ChartTooltip } from '@klear/ui/components/chart'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import { CurrencyReportSelector } from '@klear/ui/components/currency-report-selector'
import { SegmentedControl } from '@klear/ui/components/segmented-control'
import type { CurrencyCode } from '@klear/constants/currency'
import { KlearDate } from '@klear/date'
import { moneyFormatterFor } from '@klear/money/currency'
import { useInvoices } from '@klear/ui/hooks/use-invoices'
import { usePurchaseOrders } from '@klear/ui/hooks/use-purchase-orders'
import { useCurrencyScopedReport } from '@klear/ui/hooks/use-currency-scoped-report'
import {
  aggregate,
  bucketLabelFor,
  Series,
  type Granularity,
  type Lookback
} from '@/app/orders/orders-insights-aggregate'

const SERIES_META: Record<Series, { label: string; color: string }> = {
  [Series.billed]: { label: 'Invoicing', color: '#0066FF' },
  [Series.collected]: { label: 'Collections', color: '#10B981' }
}

const LOOKBACK_OPTIONS: { value: Lookback; label: string }[] = [
  { value: '6m', label: '±6 Months' },
  { value: '1y', label: '±1 Year' },
  { value: 'all', label: 'All time' }
]

const LOOKBACK_WINDOW_LABEL: Record<Lookback, string> = {
  '6m': '6 months before and after today',
  '1y': '1 year before and after today',
  all: 'all time'
}

function MetricCard({ title, value, hint }: { title: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {hint ? <p className="text-xs text-muted-foreground mt-1">{hint}</p> : null}
      </CardContent>
    </Card>
  )
}

function CurrentAwareTick({
  x,
  y,
  payload,
  currentLabel
}: {
  x?: number
  y?: number
  payload?: { value: string }
  currentLabel: string
}) {
  const value = payload?.value ?? ''
  const isCurrent = value === currentLabel
  return (
    <text
      x={x}
      y={y}
      dy={12}
      textAnchor="middle"
      fontSize={12}
      style={isCurrent ? { fill: 'var(--foreground)', fontWeight: 600 } : undefined}
    >
      {value}
    </text>
  )
}

function OrdersByPeriodChart({
  data,
  currency,
  granularity,
  onGranularityChange
}: {
  data: Array<{ label: string; billed: number; collected: number }>
  currency: CurrencyCode
  granularity: Granularity
  onGranularityChange: (granularity: Granularity) => void
}) {
  const formatMoney = moneyFormatterFor(currency)
  const currentLabel = bucketLabelFor(KlearDate.today(), granularity)
  const seriesLabel = (name: string): string => {
    if (name === Series.billed) return SERIES_META[Series.billed].label
    if (name === Series.collected) return SERIES_META[Series.collected].label
    return name
  }
  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {[Series.billed, Series.collected].map((series) => (
            <span key={series} className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: SERIES_META[series].color }} />
              {SERIES_META[series].label}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-6">
          <SegmentedControl<Granularity>
            options={[
              { value: 'month', label: 'Month' },
              { value: 'quarter', label: 'Quarter' }
            ]}
            value={granularity}
            onChange={onGranularityChange}
          />
        </div>
      </CardHeader>
      <CardContent>
        <ChartContainer config={SERIES_META} className="h-65 w-full">
          <BarChart data={data} margin={{ left: 45, right: 15, top: 10, bottom: 10 }}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tick={<CurrentAwareTick currentLabel={currentLabel} />}
            />
            <YAxis
              width={60}
              tickFormatter={(value: number) => formatMoney(value, { notation: 'compact' })}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 12 }}
            />
            <ChartTooltip
              cursor={{ fill: 'rgba(0,0,0,0.04)' }}
              wrapperStyle={{ outline: 'none' }}
              formatter={(value, name) => [formatMoney(value as number), seriesLabel(`${name}`)]}
            />
            <Bar dataKey="billed" fill={SERIES_META[Series.billed].color} radius={[4, 4, 0, 0]} />
            <Bar dataKey="collected" fill={SERIES_META[Series.collected].color} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}

export function OrdersInsights() {
  const { traderId } = useParams()
  const traderIdNum = parseInt(traderId ?? '0', 10)
  const filters = traderIdNum > 0 ? { traderId: traderIdNum } : {}

  const { purchaseOrders } = usePurchaseOrders(filters)
  const { invoices } = useInvoices(filters)

  const [granularity, setGranularity] = useState<Granularity>('month')
  const [lookback, setLookback] = useState<Lookback>('6m')

  const poMonies = useMemo(() => purchaseOrders.map((po) => po.amount), [purchaseOrders])
  const invoiceMonies = useMemo(() => invoices.map((invoice) => invoice.amount), [invoices])

  const { selectedCurrency, setSelectedCurrency, showCurrencySelector, availableCurrencies } = useCurrencyScopedReport([
    poMonies,
    invoiceMonies
  ])

  const { totalOrderAmount, totalInvoiced, buckets } = useMemo(
    () =>
      aggregate({
        purchaseOrders,
        invoices,
        selectedCurrency,
        granularity,
        lookback
      }),
    [purchaseOrders, invoices, selectedCurrency, granularity, lookback]
  )

  const chartData = useMemo(
    () =>
      [...buckets.values()].map((b) => ({
        label: b.label,
        billed: moneyChartValue(b.billed),
        collected: moneyChartValue(b.collected)
      })),
    [buckets]
  )

  const pctBilled = totalOrderAmount.isZero()
    ? null
    : BasisPoints.fromMoney(totalInvoiced, totalOrderAmount).toString({ digits: 0 })

  return (
    <section className="flex flex-col gap-6">
      <div className="flex items-center justify-end gap-3">
        {showCurrencySelector && (
          <CurrencyReportSelector
            value={selectedCurrency}
            onChange={setSelectedCurrency}
            currencies={availableCurrencies}
          />
        )}
        <SegmentedControl<Lookback> options={LOOKBACK_OPTIONS} value={lookback} onChange={setLookback} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <MetricCard
          title="Total Order Amount"
          value={totalOrderAmount.toString({ notation: 'compact' })}
          hint={`Purchase orders effective in ${LOOKBACK_WINDOW_LABEL[lookback]}`}
        />
        <MetricCard
          title="Total Invoiced"
          value={totalInvoiced.toString({ notation: 'compact' })}
          hint={
            pctBilled != null
              ? `${pctBilled} of order amount invoiced`
              : `Invoices issued in ${LOOKBACK_WINDOW_LABEL[lookback]}`
          }
        />
      </div>
      <OrdersByPeriodChart
        data={chartData}
        currency={selectedCurrency}
        granularity={granularity}
        onGranularityChange={setGranularity}
      />
    </section>
  )
}
